import React, { useMemo } from "react";

const MAX_TICKS = 48;
const FALLBACK_COLOR = "#738090";

const TICKS_CSS = `
.inventory-status-ticks {
  width: max-content;
  max-width: min(320px, 42vw);
  min-width: 0;
}
.inventory-status-ticks__bar {
  display: flex;
  align-items: stretch;
  gap: 2px;
  height: 14px;
  overflow: hidden;
}
.inventory-status-ticks__tick {
  display: block;
  width: 3px;
  flex: 0 0 3px;
  height: 100%;
}
.inventory-status-ticks__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 8px;
  margin-top: 2px;
  font-size: 10px;
  line-height: 1.15;
}
.inventory-status-ticks__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}
.inventory-status-ticks__legend-dash {
  display: inline-block;
  width: 3px;
  height: 9px;
  flex: 0 0 3px;
}
.white-content .inventory-status-ticks__legend {
  color: #32325d;
}
body:not(.white-content) .inventory-status-ticks__legend {
  color: rgba(255, 255, 255, 0.78);
}
.rt-pagination-modern__extra {
  min-width: 0;
}
.rt-pagination-modern__left .inventory-status-ticks {
  margin-top: 0;
}
`;

const buildTicks = (items) => {
  const total = items.reduce((sum, item) => sum + (item.count || 0), 0);
  if (!items.length) return [];
  if (total <= 0) {
    return items.map((item) => ({
      key: item.value,
      color: item.color || FALLBACK_COLOR,
    }));
  }

  const maxTicks = Math.max(items.length, Math.min(MAX_TICKS, total));
  const raw = items.map((item) => ({
    ...item,
    exact: ((item.count || 0) / total) * maxTicks,
  }));
  const allocated = raw.map((item) => ({
    ...item,
    n: item.count > 0 ? Math.max(1, Math.floor(item.exact)) : 0,
  }));
  let used = allocated.reduce((sum, item) => sum + item.n, 0);

  const byRemainder = allocated
    .map((item, index) => ({ index, rem: raw[index].exact - Math.floor(raw[index].exact) }))
    .sort((a, b) => b.rem - a.rem);

  let i = 0;
  while (used < maxTicks && i < allocated.length * 4) {
    const target = allocated[byRemainder[i % byRemainder.length].index];
    if (target.count > 0) {
      target.n += 1;
      used += 1;
    }
    i += 1;
  }

  while (used > maxTicks) {
    const richest = allocated.reduce((best, item) => (item.n > best.n ? item : best));
    if (richest.n <= 1) break;
    richest.n -= 1;
    used -= 1;
  }

  return allocated.flatMap((item) =>
    Array.from({ length: item.n }, (_, tickIndex) => ({
      key: `${item.value}-${tickIndex}`,
      color: item.color || FALLBACK_COLOR,
    }))
  );
};

/**
 * Build status tick counts from the current page `results` (no extra API calls).
 * Counts reflect this page only — same scope as the table pagination.
 */
export const useFilterStatusCounts = ({
  selectedStatuses = [],
  allStatuses = [],
  results = [],
  getStatusValue,
  enabled = true,
}) =>
  useMemo(() => {
    if (!enabled || typeof getStatusValue !== "function") return [];

    const counts = {};
    (results || []).forEach((row) => {
      const value = getStatusValue(row);
      if (!value) return;
      counts[value] = (counts[value] || 0) + 1;
    });

    const isEmptyFilter = selectedStatuses.length === 0;
    const statusesToShow = isEmptyFilter ? allStatuses : selectedStatuses;

    const counted = statusesToShow.map((status) => ({
      ...status,
      count: counts[status.value] || 0,
    }));

    if (isEmptyFilter) {
      return counted
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count);
    }

    return counted.filter((item) => item.count > 0);
  }, [enabled, selectedStatuses, allStatuses, results, getStatusValue]);

export const useInventoryStatusFilterCounts = ({
  selectedStatuses = [],
  allStatuses = [],
  results = [],
  enabled = true,
}) =>
  useFilterStatusCounts({
    selectedStatuses,
    allStatuses,
    results,
    enabled,
    getStatusValue: (row) =>
      row?.inventory_vehicle?.vehicle_status?.status_key ||
      row?.inventory_vehicle?.vehicle_status?.value ||
      "",
  });

const InventoryStatusTicks = ({ items = [] }) => {
  const visibleItems = useMemo(
    () => items.filter((item) => item && item.value),
    [items]
  );
  const ticks = useMemo(() => buildTicks(visibleItems), [visibleItems]);
  const legendItems = useMemo(() => visibleItems.slice(0, 4), [visibleItems]);

  if (!visibleItems.length) return null;

  return (
    <>
      <style>{TICKS_CSS}</style>
      <div className="inventory-status-ticks" aria-label="Filtered status">
        <div className="inventory-status-ticks__bar">
          {ticks.map((tick) => (
            <span
              key={tick.key}
              className="inventory-status-ticks__tick"
              style={{ backgroundColor: tick.color }}
            />
          ))}
        </div>
        <div className="inventory-status-ticks__legend">
          {legendItems.map((item) => (
            <span key={item.value} className="inventory-status-ticks__legend-item">
              <span
                className="inventory-status-ticks__legend-dash"
                style={{ backgroundColor: item.color || FALLBACK_COLOR }}
              />
              <span>
                {item.count ?? 0} {item.label}
              </span>
            </span>
          ))}
        </div>
      </div>
    </>
  );
};

export default InventoryStatusTicks;
